import { ChangeEvent, FC, useCallback, useContext, useEffect, useReducer, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Card, CardContent, Container, Grid, TextField } from "@mui/material";
import { flyInContent, flyInMainWrapper } from "../../../../../AppGlobalStyles";
import FlyInDrawerHeader from "../../../../Common/FlyInDrawerHeader/FlyInDrawerHeader";
import ViewItem from "./ViewItem";
import { infiniteScrollReducer } from "../ViewSelectorReducers";
import { GenericGridConfigurationContext } from "../../../../../contexts/GenericGridConfigurationContext/GenericGridConfigurationContext";
import { GenericProGridContext } from "../../../GenericProGridContext/GenericProGridContext";
import { GRID_IDENTIFIERS } from "../../../../../contexts/types";
import { isCancelledErrorProcessor } from "../../../../../utils/AppUtils";
import StaticViewItem from "./StaticViewItem";

interface ViewListFlyinProps {
    editHandler: (config) => void;
    copyHandler: (config) => void;
    deleteHandler: (config, onDeleteComplete: () => void) => void;
    closeDrawer: () => void;
    createBlankView: () => void;
}

const ViewListFlyin: FC<ViewListFlyinProps> = ({ editHandler, copyHandler, deleteHandler, closeDrawer, createBlankView }) => {
    const searchTimer = useRef<NodeJS.Timeout | null>(null);
    const viewsContainer = useRef<HTMLDivElement>(null);

    const { configManager } = useContext(GenericGridConfigurationContext);
    const { identifier: gridIdentifier, activeGridConfigMetadata, setActiveGridConfigMetadata, setIncomingGridState } = useContext(GenericProGridContext);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [scrollState, dispatchScrollState] = useReducer(infiniteScrollReducer, {
        data: [],
        lastLoadedPageData: null,
        searchString: "",
        inputFieldState: "",
    });

    const onSearchChange = useCallback(
        (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            dispatchScrollState({ type: "inputFieldState", payload: e.target.value });

            if (searchTimer.current) {
                clearTimeout(searchTimer.current);
            }

            searchTimer.current = setTimeout(() => {
                dispatchScrollState({ type: "searchString", payload: e.target.value });
            }, 1000);
        },
        [scrollState.inputFieldState],
    );

    const selectConfigHandler = (config) => {
        setActiveGridConfigMetadata({ id: config.id, name: config.name, isGlobal: config.isGlobal });
        setIncomingGridState(config.configuration);
        closeDrawer();
    };

    useEffect(() => {
        if (!gridIdentifier || !configManager) {
            if (scrollState.lastLoadedPageData) {
                dispatchScrollState({ type: "reset", payload: undefined });
            }

            return undefined;
        }

        if (scrollState.lastLoadedPageData?.lastPage) {
            return undefined;
        }

        const abortController = new AbortController();

        function loadData(identifier: `${GRID_IDENTIFIERS}`, page: number, spinner: boolean, queryString: string) {
            setIsLoading(true);
            return configManager
                ?.getGridConfigsByIdentifier(
                    identifier,
                    {
                        signal: abortController.signal,
                        params: {
                            name: queryString,
                            page,
                            size: 20,
                        },
                    },
                    spinner,
                )
                .then((res) => {
                    dispatchScrollState({ type: "loadPage", payload: res.data });
                    setIsLoading(false);
                })
                .catch(isCancelledErrorProcessor);
        }

        if (!scrollState.lastLoadedPageData) {
            loadData(gridIdentifier, 0, true, scrollState.searchString);
            return () => abortController.abort();
        }

        let loading: boolean;

        async function onScroll(e: Event) {
            const { scrollTop, scrollHeight, clientHeight } = e.currentTarget as HTMLDivElement;
            const percentage = Math.trunc(scrollTop) / (scrollHeight - clientHeight);

            if (percentage >= 0.9 && !loading) {
                loading = true;
                await loadData(gridIdentifier, scrollState.lastLoadedPageData.pageNumber + 1, false, scrollState.searchString);
                loading = false;
            }
        }

        const container = viewsContainer.current;

        if (container) {
            container.addEventListener("scroll", onScroll);
        }
        return () => {
            if (container) {
                container.removeEventListener("scroll", onScroll);
            }
        };
    }, [gridIdentifier, scrollState, configManager]);

    return (
        <Container sx={flyInMainWrapper}>
            <FlyInDrawerHeader
                close={closeDrawer}
                title={{ id: "generic-pro-grid.view-selector.title.select-custom-grid-view", defaultMessage: "Select Custom Grid View" }}
            />
            <Grid container sx={{ ...flyInContent, paddingTop: "calc(1rem - 8px)" }}>
                <Card
                    sx={{
                        boxShadow: "none",
                        width: "100%",
                        marginBottom: 0,
                        display: "flex",
                        flexDirection: "column",
                        flexGrow: 1,
                        maxHeight: "100%",
                        borderRadius: "unset",
                        // This Card is a layout container only (no shadow, no radius) — it must not paint a
                        // surface of its own. A Paper inside the drawer resolves to a DIFFERENT dark tone than
                        // the drawer itself, because MUI derives the dark-mode lift from elevation
                        // (Card = 1, Drawer = 16), and the mismatch draws a visible inset frame around the
                        // content. backgroundImage also has to go: that is where MUI puts --Paper-overlay.
                        backgroundColor: "transparent",
                        backgroundImage: "none",
                    }}
                >
                    <CardContent sx={{ display: "flex", flexGrow: 1, maxHeight: "100%", height: "100px", padding: "0 !important" }}>
                        <CardContent sx={{ padding: "0 !important", display: "flex", flexDirection: "column", maxHeight: "100%", width: "100%" }}>
                            <TextField
                                sx={{ marginTop: "8px", marginBottom: "8px", zIndex: 99999 }}
                                fullWidth
                                label={<FormattedMessage id="generic-pro-grid.view-selector.label.search-by-name" defaultMessage="Search by name" />}
                                placeholder="Search by name"
                                onChange={onSearchChange}
                                margin="dense"
                                size="small"
                                value={scrollState.inputFieldState}
                            />
                            <StaticViewItem key={"creating item"} selectElement={createBlankView} elementName={"Create View"} />
                            <div ref={viewsContainer} style={{ margin: "16px 0 0 0", maxHeight: "100%", overflow: "auto", height: "100%" }}>
                                {scrollState.data.length
                                    ? scrollState.data.map((config) => {
                                          return (
                                              <ViewItem
                                                  key={config.id}
                                                  selected={config.id === activeGridConfigMetadata?.id}
                                                  selectElement={() => selectConfigHandler(config)}
                                                  elementName={config.name}
                                                  isGlobal={config.isGlobal}
                                                  editHandler={() => editHandler(config)}
                                                  copyHandler={() => copyHandler(config)}
                                                  deleteHandler={() => deleteHandler(config, () => dispatchScrollState({ type: "reset", payload: undefined }))}
                                              />
                                          );
                                      })
                                    : !isLoading && <FormattedMessage id="generic-pro-grid.view-selector.label.no-results" defaultMessage="No Results" />}
                            </div>
                        </CardContent>
                    </CardContent>
                </Card>
            </Grid>
        </Container>
    );
};

export default ViewListFlyin;
